// src/services/authService.js
//
// Session administrateur : login, GET /admin/me, logout local.
// Le backend (Worker Admin) reste l’autorité pour les autorisations.

import {
  ADMIN_WORKER_URL,
  setAdminAuthToken,
  getAdminAuthToken,
  removeAdminAuthToken,
  clearAdminLocalSession,
} from '../config/api.config';

const SESSION_KEY = 'dsi_admin_session';
const USER_KEY = 'dsi_admin_user';

/**
 * Persiste la réponse réelle de GET /admin/me (sans inventer de champs).
 * Structure attendue (contrat) :
 * {
 *   administrateur: { id, email, niveau },
 *   roles: [...],
 *   permissions: [...]
 * }
 */
function persistMePayload(data) {
  if (!data || typeof data !== 'object') return;

  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }

  // Compatibilité : miroir de l’objet administrateur si présent
  if (data.administrateur && typeof data.administrateur === 'object') {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(data.administrateur));
    } catch {
      /* ignore */
    }
  }
}

function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export const authService = {
  async login(email, mot_de_passe) {
    let response;
    try {
      response = await fetch(`${ADMIN_WORKER_URL}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password: mot_de_passe }),
      });
    } catch (err) {
      throw new Error('Impossible de joindre le Worker Admin. Vérifiez votre connexion.');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Email ou mot de passe incorrect.');
      }
      if (response.status === 403) {
        throw new Error('Accès refusé. Votre compte administrateur est restreint.');
      }
      if (response.status >= 500) {
        throw new Error('Le Worker Admin rencontre un problème interne. Réessayez plus tard.');
      }
      throw new Error(data.error || `Erreur lors de la connexion (${response.status})`);
    }

    if (!data.token) {
      throw new Error("Réponse invalide du Worker Admin : jeton d'accès manquant.");
    }

    setAdminAuthToken(data.token);

    // Login peut ne renvoyer que le token : identité via GET /admin/me
    if (data.administrateur) {
      persistMePayload({
        administrateur: data.administrateur,
        roles: Array.isArray(data.roles) ? data.roles : [],
        permissions: Array.isArray(data.permissions) ? data.permissions : [],
      });
    }

    try {
      await this.fetchMe();
    } catch {
      // Token posé ; ProtectedRoute revalidera via verifySession
    }

    return data;
  },

  /**
   * Point unique de déconnexion côté frontend.
   * Pas d’appel /admin/logout (hors contrat vérifié).
   * Purge token + session identité/rôles/permissions + clés legacy.
   */
  logout() {
    clearAdminLocalSession();
  },

  /**
   * GET /admin/me — récupère et conserve le payload réel.
   * @returns {Promise<object>} corps de réponse
   */
  async fetchMe() {
    const token = getAdminAuthToken();
    if (!token) {
      throw new Error('Non authentifié');
    }

    let response;
    try {
      response = await fetch(`${ADMIN_WORKER_URL}/admin/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      throw new Error('Impossible de joindre le Worker Admin.');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        this.logout();
      }
      const err = new Error(data.error || data.erreur || `Erreur session (${response.status})`);
      err.status = response.status;
      err.body = data;
      throw err;
    }

    persistMePayload(data);
    return data;
  },

  /**
   * Garde-route : valide le token via GET /admin/me et met à jour la session locale.
   * @returns {Promise<boolean>}
   */
  async verifySession() {
    const token = getAdminAuthToken();
    if (!token) {
      return false;
    }

    try {
      await this.fetchMe();
      return true;
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        return false;
      }
      // Panne réseau : ne pas déconnecter brutalement
      return true;
    }
  },

  /** Payload complet de la dernière réponse /admin/me (ou null). */
  getSession() {
    return readSession();
  },

  /**
   * Identité administrateur (objet `administrateur` du contrat), ou null.
   * Ne reconstruit pas de schéma : lit uniquement ce qui a été persisté.
   */
  getCurrentUser() {
    const session = readSession();
    if (session?.administrateur && typeof session.administrateur === 'object') {
      return session.administrateur;
    }
    try {
      const legacy = localStorage.getItem(USER_KEY);
      return legacy ? JSON.parse(legacy) : null;
    } catch {
      return null;
    }
  },

  /** Tableau `roles` tel que renvoyé par /admin/me, sinon []. */
  getRoles() {
    const session = readSession();
    return Array.isArray(session?.roles) ? session.roles : [];
  },

  /** Tableau `permissions` effectives tel que renvoyé par /admin/me, sinon []. */
  getPermissions() {
    const session = readSession();
    return Array.isArray(session?.permissions) ? session.permissions : [];
  },

  /**
   * Indique si une permission apparaît dans la liste effective locale.
   * L’UI n’est jamais une sécurité : le Worker Admin reste autoritaire (403).
   */
  hasPermission(code) {
    if (!code) return false;
    const perms = this.getPermissions();
    const needle = String(code);
    return perms.some((p) => {
      if (p == null) return false;
      if (typeof p === 'string') return p === needle;
      if (typeof p === 'object') {
        return (
          p.code === needle ||
          p.permission === needle ||
          p.nom === needle ||
          p.name === needle
        );
      }
      return false;
    });
  },
};
